/**
 * useWorkEmail - Hook for managing work email identities in Total Connect
 *
 * All Total Connect features (calendar, messenger, calls, cloud storage, teams)
 * require users to have an active mailbox. This hook provides:
 *
 * 1. The user's primary work email for the current client
 * 2. Access status (whether user can use Total Connect features)
 * 3. Team members with their work emails (not personal emails)
 *
 * Offline: last-known mailbox snapshot is read from TotalAccessOfflineDB (`workEmailAccessCache`)
 * so TotalConnectAccessGate can render children immediately while the API refreshes.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/lib/authService';
import {
  readCachedWorkEmailAccess,
  writeCachedWorkEmailAccess,
} from '@/lib/offline/workEmailAccessCache';

export interface WorkEmailAccount {
  id: number;
  email_address: string;
  display_name?: string;
  is_primary: boolean;
  domain_name?: string;
  mailcow_status: 'pending' | 'active' | 'suspended' | 'error' | 'virtual';
  is_virtual?: boolean;
  quota_mb?: number;
  quota_used_mb?: number;
}

export interface TeamMemberWithWorkEmail {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  work_email: string;
  personal_email?: string;
  display_name?: string;
  avatar_url?: string;
  role?: string;
  department?: string;
  title?: string;
  last_activity_at?: string;
  show_when_online?: boolean;
  is_online?: boolean;
}

interface UseWorkEmailReturn {
  workEmail: WorkEmailAccount | null;
  hasActiveMailbox: boolean;
  isLoading: boolean;
  error: string | null;

  canAccessTotalConnect: boolean;
  accessBlockedReason: string | null;

  teamMembers: TeamMemberWithWorkEmail[];
  loadingTeamMembers: boolean;

  refreshWorkEmail: () => Promise<void>;
  fetchTeamMembersWithWorkEmails: (teamId?: string) => Promise<TeamMemberWithWorkEmail[]>;
  getWorkEmailForUser: (userId: number) => Promise<string | null>;
}

export function useWorkEmail(): UseWorkEmailReturn {
  const { client, isAuthenticated } = useAuth();

  const [workEmail, setWorkEmail] = useState<WorkEmailAccount | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberWithWorkEmail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasActiveMailbox = useMemo(() => {
    return (
      workEmail?.mailcow_status === 'active' ||
      workEmail?.mailcow_status === 'virtual' ||
      workEmail?.is_virtual === true
    );
  }, [workEmail]);

  const canAccessTotalConnect = useMemo(() => {
    return hasActiveMailbox;
  }, [hasActiveMailbox]);

  const accessBlockedReason = useMemo(() => {
    if (!workEmail) {
      return 'You need a work email account to use Total Connect. Please contact your administrator to set up your mailbox.';
    }
    if (workEmail.mailcow_status === 'pending') {
      return 'Your mailbox is being set up. Please wait a few minutes and try again.';
    }
    if (workEmail.mailcow_status === 'suspended') {
      return 'Your mailbox has been suspended. Please contact your administrator.';
    }
    if (workEmail.mailcow_status === 'error') {
      return 'There is an issue with your mailbox. Please contact your administrator.';
    }
    return null;
  }, [workEmail]);

  const fetchWorkEmailFromNetwork = useCallback(async (): Promise<WorkEmailAccount | null> => {
    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      throw new Error('No authentication token');
    }

    const response = await fetch('/api/v1/total-connect/work-email', {
      headers: {
        Authorization: `Bearer ${tokens.access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.workEmail) {
      return data.workEmail as WorkEmailAccount;
    }

    if (data.error) {
      setError(data.error);
    }
    return null;
  }, []);

  const refreshWorkEmail = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    if (!client?.id) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const next = await fetchWorkEmailFromNetwork();
      setWorkEmail(next);
      await writeCachedWorkEmailAccess(client.id, next);
    } catch (err) {
      console.error('[useWorkEmail] Error fetching work email:', err);
      setError('Failed to fetch work email');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, client?.id, fetchWorkEmailFromNetwork]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!isAuthenticated) {
        setIsLoading(true);
        return;
      }

      if (!client?.id) {
        setWorkEmail(null);
        setIsLoading(false);
        return;
      }

      const cached = await readCachedWorkEmailAccess(client.id);
      if (cancelled) return;

      if (cached && Object.prototype.hasOwnProperty.call(cached, 'workEmail')) {
        setWorkEmail(cached.workEmail);
        setIsLoading(false);
      } else {
        setIsLoading(true);
      }

      setError(null);

      try {
        const next = await fetchWorkEmailFromNetwork();
        if (cancelled) return;
        setWorkEmail(next);
        await writeCachedWorkEmailAccess(client.id, next);
      } catch (err) {
        console.error('[useWorkEmail] Error fetching work email:', err);
        if (!cancelled) {
          setError('Failed to fetch work email');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, client?.id, fetchWorkEmailFromNetwork]);

  const fetchTeamMembersWithWorkEmails = useCallback(
    async (teamId?: string): Promise<TeamMemberWithWorkEmail[]> => {
      if (!isAuthenticated || !client?.id) {
        return [];
      }

      setLoadingTeamMembers(true);

      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) {
          throw new Error('No authentication token');
        }

        const url = teamId
          ? `/api/v1/total-connect/team-members?team_id=${teamId}`
          : '/api/v1/total-connect/team-members';

        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.members) {
          setTeamMembers(data.members);
          return data.members;
        }

        return [];
      } catch (err) {
        console.error('[useWorkEmail] Error fetching team members:', err);
        return [];
      } finally {
        setLoadingTeamMembers(false);
      }
    },
    [isAuthenticated, client?.id],
  );

  const getWorkEmailForUser = useCallback(
    async (userId: number): Promise<string | null> => {
      if (!isAuthenticated || !client?.id) {
        return null;
      }

      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) {
          return null;
        }

        const response = await fetch(`/api/v1/total-connect/work-email?user_id=${userId}`, {
          headers: {
            Authorization: `Bearer ${tokens.access_token}`,
          },
        });

        if (!response.ok) {
          return null;
        }

        const data = await response.json();

        if (data.success && data.workEmail) {
          return data.workEmail.email_address;
        }

        return null;
      } catch (err) {
        console.error('[useWorkEmail] Error fetching work email for user:', err);
        return null;
      }
    },
    [isAuthenticated, client?.id],
  );

  return {
    workEmail,
    hasActiveMailbox,
    isLoading,
    error,
    canAccessTotalConnect,
    accessBlockedReason,
    teamMembers,
    loadingTeamMembers,
    refreshWorkEmail,
    fetchTeamMembersWithWorkEmails,
    getWorkEmailForUser,
  };
}

export default useWorkEmail;
